import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import { mangas } from "@/mocks/manga";

export default function MangaDetail() {
  const { id } = useParams<{ id: string }>();
  const manga = mangas.find((m) => m.id === id);

  useEffect(() => {
    if (manga) {
      document.title = `${manga.title} - 樱花漫画网 | 免费${manga.categoryName}在线全集`;
    }
  }, [manga]);

  if (!manga) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要找的漫画不存在或已被下架。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-all whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedMangas = mangas
    .filter((m) => m.id !== manga.id && m.category === manga.category)
    .slice(0, 4);

  const chapterList = Array.from({ length: Math.min(manga.chapters, 20) }, (_, i) => ({
    num: manga.chapters - i,
    title: `第${manga.chapters - i}话`,
    date: `2026-0${Math.max(1, 8 - Math.ceil(i / 4))}-${String(Math.max(1, 30 - i * 2)).padStart(2, "0")}`,
  }));

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 bg-background-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: `免费${manga.categoryName}`, to: `/category/${manga.category}` },
              { label: manga.title },
            ]}
          />

          <article className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden">
            <div className="flex flex-col md:flex-row gap-6 p-5 md:p-8">
              <div className="w-full md:w-64 flex-shrink-0">
                <div className="aspect-[3/4] rounded-xl overflow-hidden">
                  <img
                    src={manga.cover}
                    alt={manga.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary-100 text-primary-700">
                    {manga.categoryName}
                  </span>
                  <span className={`px-2.5 py-1 rounded-md text-xs font-medium ${
                    manga.status === "已完结" ? "bg-secondary-100 text-secondary-700" : "bg-accent-100 text-accent-700"
                  }`}>
                    {manga.status}
                  </span>
                </div>

                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  {manga.title}
                </h1>

                <div className="flex items-center gap-4 text-sm text-foreground-500 mb-4 flex-wrap">
                  <span>作者：<strong className="text-foreground-900">{manga.author}</strong></span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-yellow-500 text-sm"></i>
                    <strong className="text-foreground-900">{manga.rating}</strong> 分
                  </span>
                  <span>{manga.views.toLocaleString()} 次阅读</span>
                  <span>共 {manga.chapters} 话</span>
                </div>

                <div className="flex items-center gap-1.5 mb-4 flex-wrap">
                  {manga.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-md text-xs bg-secondary-100 text-secondary-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-xl p-4 mb-5">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{manga.description}</p>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <button className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-all whitespace-nowrap">
                    <i className="ri-play-circle-line text-lg"></i>
                    开始阅读
                  </button>
                  <button className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-background-100 border border-background-200 hover:border-primary-200 text-foreground-700 hover:text-primary-600 font-medium text-sm transition-all whitespace-nowrap">
                    <i className="ri-heart-line"></i>
                    收藏
                  </button>
                  <button className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-background-100 border border-background-200 hover:border-primary-200 text-foreground-700 hover:text-primary-600 font-medium text-sm transition-all whitespace-nowrap">
                    <i className="ri-share-forward-line"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>

            <div className="border-t border-background-200 px-5 md:px-8 py-6">
              <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-list-check text-primary-500"></i>
                章节列表
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {chapterList.map((ch) => (
                  <div
                    key={ch.num}
                    className="flex items-center justify-between p-3 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-200 hover:bg-background-50 cursor-pointer transition-all group"
                  >
                    <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors font-medium">
                      {ch.title}
                    </span>
                    <span className="text-xs text-foreground-400">{ch.date}</span>
                  </div>
                ))}
              </div>
              {manga.chapters > 20 && (
                <div className="text-center mt-4">
                  <button className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-background-100 border border-background-200 hover:border-primary-200 text-sm text-foreground-600 hover:text-primary-600 transition-all whitespace-nowrap">
                    查看全部 {manga.chapters} 话
                    <i className="ri-arrow-down-s-line"></i>
                  </button>
                </div>
              )}
            </div>
          </article>

          {relatedMangas.length > 0 && (
            <section className="mt-10">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-1 h-5 rounded-full bg-primary-500"></div>
                <h2 className="text-lg font-bold text-foreground-950">
                  相关推荐
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {relatedMangas.map((rm) => (
                  <Link
                    key={rm.id}
                    to={`/manga/${rm.id}`}
                    className="group block p-3 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="aspect-[3/4] rounded-lg overflow-hidden mb-3">
                      <img
                        src={rm.cover}
                        alt={rm.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {rm.title}
                    </h3>
                    <div className="flex items-center gap-1 mt-1 text-xs text-foreground-400">
                      <span className="text-yellow-500">
                        <i className="ri-star-fill text-xs"></i> {rm.rating}
                      </span>
                      <span>·</span>
                      <span>{rm.categoryName}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                headline: `${manga.title} - 免费${manga.categoryName}在线全集`,
                description: manga.description,
                author: {
                  "@type": "Person",
                  name: manga.author,
                },
                publisher: {
                  "@type": "Organization",
                  name: "樱花漫画网",
                },
                datePublished: "2020-01-01",
                dateModified: manga.updatedAt,
                image: manga.cover,
                articleSection: manga.categoryName,
                keywords: manga.tags.join(","),
              }),
            }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "首页",
                    item: window.location.origin,
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: `免费${manga.categoryName}`,
                    item: `${window.location.origin}/category/${manga.category}`,
                  },
                  {
                    "@type": "ListItem",
                    position: 3,
                    name: manga.title,
                  },
                ],
              }),
            }}
          />
        </div>
      </main>
      <Footer />
    </>
  );
}